import React from 'react';
import { detectXLinks, detectAllLinks, isXUrl, detectYouTubeLinks, isYouTubeUrl } from '../../utils/linkUtils';
import XLinkPreview from './XLinkPreview';
import YouTubeLinkPreview from './YouTubeLinkPreview';

interface LinkEnhancedContentProps {
  content: string;
  className?: string;
}

export const LinkEnhancedContent: React.FC<LinkEnhancedContentProps> = ({ 
  content, 
  className = '' 
}) => {
  // Détecter tous les liens dans le contenu
  const allLinks = detectAllLinks(content);
  const xLinks = detectXLinks(content);
  const youtubeLinks = detectYouTubeLinks(content);
  
  // Séparer le texte et les liens pour le rendu
  const renderContentWithLinks = () => {
    if (allLinks.length === 0) {
      return <p className="whitespace-pre-wrap leading-relaxed">{content}</p>;
    }

    // Remplacer les liens par des placeholders temporaires pour le rendu
    let processedContent = content;
    const linkPlaceholders: { [key: string]: string } = {};

    allLinks.forEach((link, index) => {
      const placeholder = `__LINK_${index}__`;
      linkPlaceholders[placeholder] = link;
      processedContent = processedContent.replace(link, placeholder);
    });

    // Diviser le contenu en segments
    const segments = processedContent.split(/(__LINK_\d+__)/);

    return (
      <p className="whitespace-pre-wrap leading-relaxed">
        {segments.map((segment, index) => {
          if (segment.startsWith('__LINK_') && segment.endsWith('__')) {
            const originalLink = linkPlaceholders[segment];
            return (
              <a
                key={index}
                href={originalLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyan-400 hover:text-cyan-300 underline transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                {originalLink}
              </a>
            );
          }
          return segment;
        })}
      </p>
    );
  };

  return (
    <div className={className}>
      {/* Contenu textuel avec liens cliquables */}
      <div className="text-emerald-100 mb-4">
        {renderContentWithLinks()}
      </div>

      {/* Prévisualisations des liens X/Twitter */}
      {xLinks.length > 0 && (
        <div className="space-y-3">
          {xLinks.map((xLink, index) => (
            <XLinkPreview 
              key={`${xLink}-${index}`} 
              url={xLink}
              className="mt-3"
            />
          ))}
        </div>
      )}

      {/* Prévisualisations des liens YouTube */}
      {youtubeLinks.length > 0 && (
        <div className="space-y-3">
          {youtubeLinks.map((youtubeLink, index) => (
            <YouTubeLinkPreview 
              key={`${youtubeLink}-${index}`} 
              url={youtubeLink}
              className="mt-3"
            />
          ))}
        </div>
      )}

      {/* Aperçu des autres liens (optionnel - pour l'avenir) */}
      {allLinks.length > xLinks.length + youtubeLinks.length && (
        <div className="mt-3">
          {allLinks
            .filter(link => !isXUrl(link) && !isYouTubeUrl(link))
            .slice(0, 2) // Limiter à 2 liens génériques
            .map((link, index) => (
              <div 
                key={`generic-${index}`}
                className="border border-emerald-800/30 rounded-lg p-3 bg-gradient-to-r from-gray-900/40 to-emerald-900/10 mb-2 last:mb-0"
              >
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-emerald-300/60 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.102m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-cyan-400 hover:text-cyan-300 text-sm truncate flex-1 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {link}
                  </a>
                  <svg className="w-4 h-4 text-emerald-300/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default LinkEnhancedContent;
